/**
 * Корневой роут TanStack (docs/MIGRATION_ROUTER.md, Фазы 1 и 3).
 *
 * С Фазы 3 здесь живут ВСЕ корневые сайд-эффекты, раньше зашитые в
 * Router.tsx (легаси-Switch): теперь они работают одинаково для
 * легаси-зоны и мигрировавших роутов, т.к. подписаны на legacyHistory
 * (адаптер поверх TanStack history — единое URL-состояние).
 *
 * RootEffects намеренно объявлен В ЭТОМ ЖЕ файле: любой отдельный
 * .tsx в src/routing генератор счёл бы файл-роутом (см.
 * routeFileIgnorePattern в buildPlugins.ts — уроки Фазы 2).
 */
// ЭТАП 1 · Шаг 3 — серверный шим globals location (ПЕРВЫМ, до всего).
import '../shims/serverLocation';
import '../shims/serverFetchUA';
// ЭТАП 1 · Шаг 3 — порт сайд-эффекта из старого entry (src/index.js, под Start
// не выполняется): русская локаль dayjs. Ставим на module-eval и на сервере, и
// на клиенте — SSR-даты («22 авг Сб») совпадают с клиентскими при гидрации.
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { getSSRDevice } from '@utils/device';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import {
  createRootRoute,
  HeadContent,
  notFound,
  Scripts,
  useRouter
} from '@tanstack/react-router';
import {
  installBuildVersionWatch,
  installTabAgeWatch
} from '@utils/buildVersion';
import { DEPLOY_RELOAD_LOG } from '@utils/deployRecovery';
import LayoutShell from '@components/Layout/Shell';
import { LegacyNotFound } from './compat/shells';
import type { IReactionDisposer } from 'mobx';
import { autorun, when } from 'mobx';
import isEmpty from 'lodash/isEmpty';
import type { History } from './compat/types';
import API from '@api';
import type { RedirectsModel } from '@schema/cart';
import { loadFlocktory } from '@utils/flocktory/loadFlocktory';
import { reInitFlocktory } from '@utils/flocktory/reInit';
import { notifications } from '@utils/notifications';
import ScriptUtil from '@utils/script';
import { loadWhenIdle } from '@utils/loadWhenIdle';
import { ensureTinkoffSdk } from '@utils/tinkoffSdk';
import { IsPrerender } from '@utils/check';
import { getLocalStorageItem } from '@utils/getLocalStorageItem';
import { markHydrated } from '@utils/ssr';
import { prefetchForPathname } from '../ssrPrefetch';
import { resolveRedirect } from '../ssrRedirects';
import { getQueryClient } from '../providers/query';
import type { CheckCartChangesQuery } from '@schema/main';
import { ErrorBoundary } from '../ErrorBoundary';
import { useStores } from '../stores';
import { legacyHistory } from './legacyHistory';
import {
  isPaymentReturnUrl,
  isTrustedPaymentOrigin,
  readPaymentReturnPath
} from '@utils/paymentReturn';
// ЭТАП 1 · Шаг 3 — провайдеры приложения переехали сюда из src/index.js
// (под Start роутер рендерит сам Start, поэтому провайдеры оборачивают его
// здесь, в __root). ToastContainer/Helmet/Store/Query — как было в index.js.
import { ToastContainer } from 'react-toastify';
import { HelmetProvider } from '@dr.pogodin/react-helmet';
import { StoreProvider } from '../storeContext';
import { QueryProvider } from '../providers/query';
// Глобальные стили (были в src/index.js — под Start их тянет граф __root)
import 'keen-slider/keen-slider.min.css';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import '../styles/styles.scss';
import '../styles/tailwind.css';
import '../styles/keenSliderSSR.scss';

dayjs.locale('ru');

const PAYMENT_RETURN_FALLBACK_MS = 2500;

const handlePaymentReturn = (): void => {
  if (!isPaymentReturnUrl(window.location.href)) {
    return;
  }

  const goBack = (): void => {
    const returnPath = readPaymentReturnPath();
    if (returnPath) {
      window.location.replace(returnPath);
    }
  };

  let opener: Window | null = null;
  try {
    opener = window.opener ?? null;
    if (opener?.closed) {
      opener = null;
    }
  } catch (error) {
    console.log('error paymentProcessEnded opener', error);
  }

  if (!opener) {
    goBack();
    return;
  }

  let isAcked = false;
  const onAck = (event: MessageEvent): void => {
    if (
      event.data === 'paymentProcessAck' &&
      isTrustedPaymentOrigin(event.origin)
    ) {
      isAcked = true;
      window.removeEventListener('message', onAck);
    }
  };
  window.addEventListener('message', onAck);

  try {
    opener.postMessage('paymentProcessEnded', '*');
  } catch (error) {
    console.log('error paymentProcessEnded', error);
    window.removeEventListener('message', onAck);
    goBack();
    return;
  }

  setTimeout(() => {
    window.removeEventListener('message', onAck);
    if (!isAcked) {
      goBack();
    }
  }, PAYMENT_RETURN_FALLBACK_MS);
};

const RootEffects: FC = () => {
  const { userStore, modalsStore, cartStore, configStore } = useStores();
  const router = useRouter();

  // Новая сборка на сервере → следующий переход (кроме оформления /cart/*)
  // полной загрузкой, а не SPA — см. src/utils/buildVersion.ts
  useEffect(() => installBuildVersionWatch(router), [router]);
  // Вкладка живёт дольше 4 часов → следующий переход полной загрузкой (те же
  // правила, включая исключение /cart/*) — см. installTabAgeWatch
  useEffect(() => installTabAgeWatch(router), [router]);

  const redirectsRef = useRef<Promise<RedirectsModel[]> | null>(null);

  /**
   * Список активных админских редиректов.
   * БАГФИКС 27.07.2026 (решение Sergei — активировать сервис): раньше
   * кэш был `useRef([])` с проверкой `if (listRef.current)` — пустой
   * массив truthy, поэтому API.getRedirects не вызывался НИКОГДА и
   * редиректы из админки не работали (задолго до миграции роутера).
   * Теперь кэш на промисе: параллельные вызовы (initial + быстрая
   * навигация) делят один запрос; при ошибке кэш сбрасывается —
   * повторная попытка на следующей навигации.
   */
  const getRedirectList = (): Promise<RedirectsModel[]> => {
    const cached = redirectsRef.current;
    if (cached) {
      return cached;
    }
    const request: Promise<RedirectsModel[]> = API.getRedirects()
      .then(data => data.getRedirectsActive ?? [])
      .catch(error => {
        console.log('error getRedirects', error);
        redirectsRef.current = null;
        return [];
      });
    redirectsRef.current = request;
    return request;
  };

  const checkURI = (): void => {
    const path = legacyHistory.location.pathname;
    let redirect: boolean | RedirectsModel = false;

    getRedirectList().then(list => {
      list.forEach(a => {
        const isReg = a?.uri_from?.includes('^');
        if (a.uri_from) {
          if (isReg) {
            const r = new RegExp(a.uri_from);

            if (r.test(path)) {
              redirect = a;
            }
          } else {
            if (path === a.uri_from) {
              redirect = a;
            }
          }
        }
      });

      if (redirect && typeof redirect === 'object') {
        const url =
          redirect.is_not_found === 1 ? '/404' : redirect.uri_to ?? '';

        // защита от пустой цели и самозацикливания (правило A→A)
        if (url && url !== path) {
          legacyHistory.replace(url);
        }
      }
    });
  };

  const handleLegacyRegisterByLink = (history: History): void => {
    const isLegacyLink = history?.location.hash?.includes('#/invite/');
    const partnerId = history?.location.hash?.replace('#/invite/', '');
    if (isLegacyLink && partnerId) {
      history?.push(`/invite/${partnerId}`);
    }
  };

  const handleLoginHash = (
    location: { hash?: string; pathname: string },
    history: History
  ): void => {
    const hash = location?.hash || location.hash;
    const isLoginHash = hash === '#login' || hash?.includes('#login');

    if (isLoginHash) {
      const { user, isLoad, isUserLoading } = userStore;

      if (!isLoad || isUserLoading) {
        return;
      }

      const isUserLoggedIn = !isEmpty(user);

      if (isUserLoggedIn) {
        notifications('Вы уже авторизованы', 'Информация');
        history.replace(location.pathname);
      } else {
        modalsStore.changeAuthModalState();
        history.replace(location.pathname);
      }
    }
  };

  useEffect(() => {
    // ЭТАП 2 — конец фазы гидрации: с этого момента новые LazyRender-маунты
    // (SPA-навигации) возвращаются к ленивому рендеру по доскроллу.
    markHydrated();

    // Сторонние скрипты, трогающие DOM, подключаем ТОЛЬКО отсюда — то есть
    // после гидрации. Из `<head>` Flocktory успевал вставить свои узлы в
    // серверную разметку раньше React'а и ронял гидрацию (разбор — в шапке
    // loadFlocktory.ts)
    loadFlocktory();

    const { initCartStore } = cartStore;

    // ЭТАП 1 · Шаг 4 — bootstrap сторов ПОСЛЕ гидрации (раньше жил в src/index.js
    // _initMainCore, который Start не грузит). Без него userStore.isLoad не станет
    // true → корзина/личные блоки крутят загрузку вечно; configStore пуст → нет
    // apikey карт и т.п.
    configStore.wakeUp();
    void userStore.getUser();
    const bootstrapInterval = setInterval(() => {
      userStore.getOrdersThatUserCanChange();
    }, 60000);

    const unlistenInit = legacyHistory.listen(location => {
      const { isUserLoading, isUserLoggedIn, isLoad } = userStore;
      if (!isLoad || isUserLoading) {
        return;
      }
      initCartStore({
        pathname: location.pathname,
        isUserLoggedIn,
        // порт из dev (мерж 06.08.2026): bar-menu персонализация
        menuPeopleId: userStore.user?.id
      });
    });

    const storesDisposer: IReactionDisposer = autorun(() => {
      const { isUserLoading, isUserLoggedIn, isLoad } = userStore;
      if (!isLoad || isUserLoading) {
        return;
      }
      initCartStore({
        pathname: location.pathname,
        isUserLoggedIn,
        menuPeopleId: userStore.user?.id
      });
    });

    handlePaymentReturn();

    handleLegacyRegisterByLink(legacyHistory);

    const initialHash = location.hash;
    if (initialHash === '#login' || initialHash?.includes('#login')) {
      when(
        () => {
          const { isLoad, isUserLoading } = userStore;
          return isLoad && !isUserLoading;
        },
        () => {
          handleLoginHash(legacyHistory.location, legacyHistory);
        }
      );
    }

    const unlistenMain = legacyHistory.listen(location => {
      try {
        if (typeof window.yaCounter5736784 !== 'undefined') {
          // Yandex
          window.yaCounter5736784.hit(location.pathname);
        }
        reInitFlocktory();
      } catch (e) {
        console.log('error send hit', e);
      }

      handleLoginHash(location, legacyHistory);
      checkURI();
    });
    checkURI();

    return () => {
      clearInterval(bootstrapInterval);
      storesDisposer();
      unlistenInit();
      unlistenMain();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * ЭТАП 1 · Шаг 4 — порт ОСТАЛЬНЫХ сайд-эффектов старого entry
   * (src/index.js + src/ChangeListener.tsx), которые Start не выполняет.
   * Всё клиентское — в useEffect (после гидрации). Не портированы намеренно:
   * бот-задержка _initMainCore (пререндер выпилен, SSR отдаёт готовый HTML),
   * чат-бот LiveTex (закомментирован и в старом entry), RouterProvider из
   * ChangeListener (роутер теперь поднимает сам Start).
   */
  useEffect(() => {
    // ── 1. Кэшбек МТС: захват mts_uid из URL (index.js, module-eval) ──
    try {
      if (location.href.includes('mts_uid=')) {
        const mtsUid = location.href.split('mts_uid=')[1].split('&')[0];
        localStorage.setItem('cashback_uid', mtsUid);
      }
    } catch (e) {
      console.log('Error cashback');
    }

    // ── 2. Внешние виджеты (index.js, DOMContentLoaded) ──
    // Tinkoff ID SDK — только заранее «прогреть» к открытию формы входа, не
    // в окне замера: форма сама догрузит его, если не успел (tinkoffSdk.ts)
    const cancelTinkoffSdk = loadWhenIdle(() => {
      if (!IsPrerender()) {
        void ensureTinkoffSdk();
      }
    });
    // Чат-виджет Rocketdata (~2,4 МБ, Vue в iframe) — не сразу, а по первому
    // действию пользователя или через 15 с после load: раньше он попадал в
    // окно замера Lighthouse и душил TTI/Speed Index (src/utils/loadWhenIdle.ts)
    const cancelChatWidget = loadWhenIdle(() => {
      if (!IsPrerender()) {
        void ScriptUtil.add(
          'https://storage.yandexcloud.net/rocketdatas3bucket/widget.js'
        );
      }
    });

    // ── 3. Модалка «уход с сайта» по mouseleave (index.js) ──
    const onMouseLeave = (): void => {
      try {
        const showleftSiteModal = getLocalStorageItem('showleftSiteModal');
        if (!showleftSiteModal) {
          modalsStore.openModalleftSite();
          localStorage.setItem('showleftSiteModal', JSON.stringify('true'));
        }
      } catch (e) {
        console.log(e);
      }
    };
    document.addEventListener('mouseleave', onMouseLeave, false);

    // ── 4. Вотчер изменения цен корзины (ChangeListener.tsx) ──
    let cancelled = false;
    let priceTimer: ReturnType<typeof setTimeout> | null = null;

    const notifyPriceChanges = (
      array: CheckCartChangesQuery['checkCartChanges']['priceChanges']
    ): void => {
      array?.forEach(item => {
        const message = `Цена на товар <b>"${
          item?.product?.title ?? ''
        }"</b> изменилась.<br />Новая цена: <b>${
          item?.price ?? ''
        }</b>&nbsp;руб.`;
        notifications(message, 'Внимание');
      });
    };

    const scheduleNextCheck = (): void => {
      if (!cancelled) {
        priceTimer = setTimeout(checkCartPriceChanges, 60000);
      }
    };

    const checkCartPriceChanges = (): void => {
      cartStore
        .checkCartChanges()
        .then((res: CheckCartChangesQuery) => {
          const { priceChanges } = res.checkCartChanges;
          if (priceChanges?.length) {
            const changed: CheckCartChangesQuery['checkCartChanges']['priceChanges'] =
              [];
            priceChanges.forEach(item => {
              const token = `DateTime(${item?.dateTime ?? ''})&&Id:${
                item?.product?.id ?? ''
              }`;
              const localToken = getLocalStorageItem<string>(token);
              if (!localToken) {
                changed.push(item);
                localStorage.setItem(token, JSON.stringify('true'));
              }
            });
            if (changed.length) {
              notifyPriceChanges(changed);
            }
          }
        })
        .then(scheduleNextCheck)
        .catch(error => {
          if (error) {
            scheduleNextCheck();
          }
        });
    };

    const priceWatcherDisposer = when(
      () => cartStore.isLoadCart,
      () => {
        checkCartPriceChanges();
      }
    );

    return () => {
      cancelled = true;
      if (priceTimer) {
        clearTimeout(priceTimer);
      }
      priceWatcherDisposer();
      document.removeEventListener('mouseleave', onMouseLeave, false);
      cancelChatWidget();
      cancelTinkoffSdk();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

/**
 * Волна E: общий Layout (шапка/подвал) живёт здесь, поверх Outlet, и
 * НЕ размонтируется на переходах — Outlet рендерится внутри <main>
 * Shell'а. Флаги конкретного роута — через staticData.layout.
 */
// ЭТАП 1 · Шаг 3 — под Start __root владеет ПОЛНЫМ HTML-документом.
// Провайдеры (Toast/Helmet/Store/Query) оборачивают приложение здесь; сам
// контент (Shell + Outlet) и клиентские эффекты (RootEffects) — внутри них.
// <HeadContent/> — точка вставки мета Start; <Scripts/> — гидрационные скрипты.
const RootComponent: FC = () => (
  <html lang='ru'>
    <head>
      <HeadContent />
      {/* ЭТАП 1 · Шаг 3 — проброс устройства (по UA) в клиент ДО гидрации,
          чтобы первый клиентский рендер useWindowSize совпал с сервером */}
      <script
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `window.__SSR_DEVICE__=${JSON.stringify(getSSRDevice())}`
        }}
      />
    </head>
    <body>
      <ToastContainer />
      <HelmetProvider>
        <StoreProvider>
          <QueryProvider>
            <ErrorBoundary>
              <RootEffects />
              <LayoutShell />
            </ErrorBoundary>
          </QueryProvider>
        </StoreProvider>
      </HelmetProvider>
      <Scripts />
    </body>
  </html>
);

/**
 * ЭТАП 2 — порт head-статики из легаси src/public/index.html (Start его не
 * использует): preconnect'ы, инлайн-лоадер Яндекс.Метрики и таймер
 * _StartTimerLoading. Инлайны отдаются в SSR-HTML, то есть метрика стартует
 * до гидрации, как в старом шаблоне.
 *
 * ЛОАДЕРА FLOCKTORY ЗДЕСЬ БОЛЬШЕ НЕТ — и это не потеря, а исправление
 * (30.08.2026). Он вставлял свои узлы в готовую серверную разметку раньше,
 * чем React успевал её гидрировать, и страница перерисовывалась целиком.
 * Теперь его вешает `loadFlocktory()` первым эффектом после гидрации; там же
 * подробный разбор. `preconnect` на их домен остаётся — соединение
 * поднимается заранее, и задержка получается близкой к нулю.
 */
/**
 * СТРАХОВКА ДО СТАРТА ПРИЛОЖЕНИЯ: не загрузился наш скрипт или стиль.
 *
 * `deployRecovery` живёт внутри бандла — если не доехал сам бандл (404 на
 * entry или его зависимость), перезагрузить вкладку некому: белый экран.
 * Этот инлайн стоит ПЕРВЫМ в <head>, ловит (в фазе захвата — события
 * загрузки не всплывают) ошибку <script>/<link rel=stylesheet|modulepreload>
 * на НАШ хешированный js/css и один раз перезагружает страницу. Работает
 * во всех браузерах, включая Safari, где Clear-Site-Data на 404 (см.
 * server.static.mjs) срабатывает не всегда.
 *
 * Только ДО гидрации (нет класса app-hydrated, его ставит markHydrated):
 * дальше ошибки чанков — зона deployRecovery, у неё точнее диагностика.
 * До гидрации страница и так нерабочая, поэтому перезагрузка безопасна
 * и на оформлении заказа.
 *
 * Ограничитель общий с deployRecovery: та же отметка `deployReloadAt` в
 * sessionStorage и те же 10 минут — два механизма не устроят цикл на двоих.
 * Лог с префиксом DEPLOY_RELOAD_LOG — по нему e2e отличает нашу перезагрузку.
 * ES5 намеренно: инлайн не транспилируется, а целимся в Safari 14.
 */
const ASSET_ERROR_RELOAD_SNIPPET =
  '(function(){' +
  "var K='deployReloadAt',C=600000;" +
  'function ours(u){var a=document.createElement("a");a.href=u;' +
  'if(a.host!==location.host)return false;var p=a.pathname;' +
  'return /\\.(m?js|css)$/.test(p)&&(p.indexOf("/assets/")===0||/\\.[A-Za-z0-9_-]{8,}\\.(m?js|css)$/.test(p));}' +
  'window.addEventListener("error",function(e){' +
  'var t=e.target;if(!t||t===window||!t.tagName)return;' +
  'if(document.documentElement.className.indexOf("app-hydrated")!==-1)return;' +
  'var u=t.tagName==="SCRIPT"?t.src:(t.tagName==="LINK"&&/stylesheet|modulepreload|preload/.test(t.rel)?t.href:"");' +
  'if(!u||!ours(u))return;' +
  'try{var l=+sessionStorage.getItem(K)||0;if(Date.now()-l<C)return;sessionStorage.setItem(K,String(Date.now()));}catch(x){return;}' +
  `console.log(${JSON.stringify(DEPLOY_RELOAD_LOG + ': asset ')}+u);` +
  'location.reload();' +
  '},true);' +
  '})();';

const START_TIMER_SNIPPET =
  "_StartTimerLoading = +new Date(); console.log('Start: ' + _StartTimerLoading);";

// 1:1 сниппет из index.html (дедуп по document.scripts сохранён)
const METRIKA_LOADER_SNIPPET =
  '(function (m, e, t, r, i, k, a) {' +
  'm[i] = m[i] || function () { (m[i].a = m[i].a || []).push(arguments); };' +
  'm[i].l = 1 * new Date();' +
  'for (var j = 0; j < document.scripts.length; j++) {' +
  'if (document.scripts[j].src === r) { return; } }' +
  'k = e.createElement(t); a = e.getElementsByTagName(t)[0];' +
  'k.async = 1; k.src = r; a.parentNode.insertBefore(k, a);' +
  "})(window, document, 'script', 'https://mc.yandex.ru/metrika/tag.js', 'ym');";

export const Route = createRootRoute({
  /**
   * ЭТАП 3 п.3 — ветка `is_not_found` админской таблицы редиректов.
   *
   * Сами 301 из таблицы отдаёт `src/server.ts` ДО рендера — им не нужны ни
   * разметка, ни данные. А вот погашенному адресу (`is_not_found = 1`) нужна
   * страница /404, отдать её умеет только роутер — поэтому эта единственная
   * ветка живёт здесь. Список тот же и уже в кэше процесса, лишнего запроса
   * в бэк не будет.
   *
   * ТОЛЬКО НА СЕРВЕРЕ: на клиенте те же правила отрабатывает `checkURI` в
   * RootEffects (адресная строка меняется через history.replace, как и
   * раньше). Дублировать их здесь значило бы гонять один и тот же список
   * дважды и ломать SPA-навигацию.
   */
  beforeLoad: async ({ location }) => {
    if (typeof window !== 'undefined') {
      return;
    }
    const decision = await resolveRedirect(location.pathname);
    if (decision?.kind === 'notFound') {
      throw notFound();
    }
  },
  // ЭТАП 2 — SSR-данные: на сервере ждём префетч запросов страницы (реестр
  // src/ssrPrefetch.ts) — контент попадает в HTML первого ответа. На клиенте
  // не блокируем навигацию (fire-and-forget): дальше приложение живёт как SPA.
  loader: async ({ location }) => {
    const prefetch = prefetchForPathname(
      getQueryClient(),
      location.pathname,
      location.searchStr ?? ''
    );
    if (typeof window === 'undefined') {
      await prefetch;
    } else {
      void prefetch;
    }
    return null;
  },
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' }
    ],
    // Фавиконки из src/public (publicDir) — раздаются с корня. В легаси
    // index.html ссылка была /src/public/favicon.svg; с publicDir канонический
    // URL — /favicon.svg (+ .ico как фолбэк для старых браузеров/ботов).
    links: [
      { rel: 'icon', href: '/favicon.svg', type: 'image/svg+xml' },
      { rel: 'icon', href: '/favicon.ico', sizes: 'any' },
      { rel: 'preconnect', href: 'https://api.delikateska.ru' },
      { rel: 'preconnect', href: 'https://storage.delikateska.ru' },
      { rel: 'preconnect', href: 'https://new-api.delikateska.ru' },
      { rel: 'preconnect', href: 'https://api.flocktory.com' },
      // Шрифты — локально из src/public/fonts (раньше @import Google Fonts
      // в styles.scss). Правила @font-face НЕ отдельным <link rel=stylesheet>
      // (он блокировал рендер), а инлайном в head().styles ниже; здесь —
      // только preload файлов первого экрана. Состав — src/public/fonts/README.md.
      // preload кириллицы и латиницы (вариативные, все веса 400–900 в одном
      // файле): они нужны на любой странице — текст первого экрана рисуется
      // сразу нужным шрифтом, без перескока swap. crossOrigin обязателен:
      // шрифты всегда запрашиваются в CORS-режиме, без него preload не
      // переиспользуется и файл качается дважды.
      {
        rel: 'preload',
        href: '/fonts/roboto-var/roboto-cyrillic-wght.woff2',
        as: 'font',
        type: 'font/woff2',
        crossOrigin: 'anonymous'
      },
      {
        rel: 'preload',
        href: '/fonts/roboto-var/roboto-latin-wght.woff2',
        as: 'font',
        type: 'font/woff2',
        crossOrigin: 'anonymous'
      }
    ],
    // @font-face Roboto инлайном: без запроса и без блокировки рендера.
    // Файлы woff2 браузер качает лениво по unicode-range (кириллица и
    // латиница — сразу, через preload выше). Строка собирается из
    // src/public/fonts/fonts.css при сборке (define в vite.start.config.mts).
    styles: [{ children: __FONTS_INLINE_CSS__ }],
    scripts: [
      // первым: слушатель должен стоять до того, как начнут грузиться
      // стили и модули
      { children: ASSET_ERROR_RELOAD_SNIPPET },
      { children: START_TIMER_SNIPPET },
      { children: METRIKA_LOADER_SNIPPET }
    ]
  }),
  component: RootComponent,
  // 404: рендерится в позиции Outlet (внутри Shell — с шапкой/подвалом,
  // как легаси catch-all <PrivateRoute component={NotFound404} is404 />)
  notFoundComponent: LegacyNotFound
});
